import type {
  IdeaVendorRedirectVendorEntry,
  IdeaVendorTabRedirectSnapshot,
} from "@/context/data.types";

export function projectRecordMatchesRouteProductId(
  project: Record<string, unknown> | null | undefined,
  routeProductId: string | number | null | undefined
): boolean {
  if (project == null || routeProductId == null) return false;
  const want = String(routeProductId).trim();
  if (!want) return false;
  for (const k of ["id", "ideaId", "productSpecificationId", "productId", "idea_id", "productSpecificationID"] as const) {
    const v = project[k as string];
    if (v != null && String(v).trim() !== "" && String(v) === want) return true;
  }
  return false;
}

export function pickFirstProductLevelStatusString(source: Record<string, unknown> | null | undefined): string {
  if (!source || typeof source !== "object") return "";
  for (const k of [
    "businessStatus",
    "BusinessStatus",
    "status",
    "Status",
    "productStatus",
    "productBusinessStatus",
    "ideaStatus",
    "workflowBusinessStatus",
    "artworkStatuses",
  ] as const) {
    const v = source[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/**
 * Builds `{ ideaId, vendors }` for global state when leaving Active Projects after a vendor-tab redirect.
 * Uses `ideaId` / `idea_id` when present, otherwise `id` (same identifier used across create-product routes).
 */
export function buildIdeaVendorTabRedirectSnapshot(
  project: Record<string, unknown> | null | undefined,
  vendorDetails: unknown[] | null | undefined
): IdeaVendorTabRedirectSnapshot | null {
  if (!project || typeof project !== "object") return null;
  const rawIdea = project.ideaId ?? project.idea_id ?? project.id;
  const ideaId = Number(rawIdea);
  if (!Number.isFinite(ideaId)) return null;

  const list = Array.isArray(vendorDetails) ? vendorDetails : [];
  const vendors: IdeaVendorRedirectVendorEntry[] = [];

  for (let i = 0; i < list.length; i += 1) {
    const raw = list[i];
    if (!raw || typeof raw !== "object") continue;
    const v = raw as Record<string, unknown>;
    const id = Number(v.vendorId ?? v.id);
    if (!Number.isFinite(id)) continue;
    const label = String(v.vendorCorporateName || v.vendorName || v.name || "").trim();
    vendors.push({
      id,
      name: label || `Vendor ${i + 1}`,
    });
  }

  return { ideaId, vendors };
}

/**
 * First matching project record for this route id (same sources as Active Project detail).
 */
export function resolveActiveProjectRecordForProductId(
  productId: string | number | null | undefined,
  ...sources: unknown[]
): Record<string, unknown> | null {
  if (productId == null || String(productId).trim() === "") return null;
  const toRec = (o: unknown) =>
    o != null && typeof o === "object" ? (o as Record<string, unknown>) : null;
  for (const src of sources) {
    const rec = toRec(src);
    if (rec && projectRecordMatchesRouteProductId(rec, productId)) return rec;
  }
  return null;
}

function ideaKeyFromRecordOrProduct(
  record: Record<string, unknown> | null | undefined,
  productId: string | number
): number | null {
  if (record && typeof record === "object") {
    const n = Number(record.ideaId ?? record.idea_id ?? record.id);
    if (Number.isFinite(n)) return n;
  }
  const n = Number(productId);
  return Number.isFinite(n) ? n : null;
}

/**
 * Vendor rows for tabs: prefer live `vendorDetails` from context when non-empty; otherwise
 * minimal rows from `ideaVendorTabRedirectSnapshot` for the same idea/product id.
 */
export function resolveVendorDetailsListForProduct(params: {
  productId: string | number;
  sources: unknown[];
  ideaVendorTabRedirectSnapshot: IdeaVendorTabRedirectSnapshot | null;
}): unknown[] {
  const { productId, sources, ideaVendorTabRedirectSnapshot } = params;
  const record = resolveActiveProjectRecordForProductId(productId, ...sources);
  const rawDetails = record?.vendorDetails;
  const fromApi = Array.isArray(rawDetails) ? (rawDetails as unknown[]) : [];
  if (fromApi.length > 0) return fromApi;

  // Explicit empty list from the server: keep [] and do not overlay snapshot (legacy behaviour).
  if (record && Array.isArray(rawDetails) && rawDetails.length === 0) {
    return [];
  }

  const ideaKey = ideaKeyFromRecordOrProduct(record, productId);
  const snap = ideaVendorTabRedirectSnapshot;
  if (!snap || snap.vendors.length === 0) return [];

  const snapIdea = Number(snap.ideaId);
  if (!Number.isFinite(snapIdea) || ideaKey == null || snapIdea !== ideaKey) return [];

  return snap.vendors.map((v) => ({
    id: String(v.id),
    vendorId: v.id,
    vendorCorporateName: v.name,
    vendorName: v.name,
  }));
}

/** Numeric idea id from explicit param, selected idea, or route segment (in that order). */
export function ideaKeyFromParams(
  ideaId: string | number | null | undefined,
  selectedIdea?: { ideaId?: unknown } | null,
  routeId?: unknown
): number | null {
  if (ideaId != null && String(ideaId).trim() !== "") {
    const n = Number(ideaId);
    if (Number.isFinite(n)) return n;
  }
  const fromIdea = Number(selectedIdea?.ideaId ?? "");
  if (Number.isFinite(fromIdea)) return fromIdea;
  const rid = Array.isArray(routeId) ? routeId[0] : routeId;
  const n = Number(rid ?? "");
  return Number.isFinite(n) ? n : null;
}

/**
 * Minimal vendor rows from `ideaVendorTabRedirectSnapshot` for the same idea id
 * (Business Review / Pre-Panel / Tasting Panel / Vendor Assessment tab strip).
 */
export function resolveIdeaPanelVendorsFromSnapshotOnly(
  ideaKey: number | null,
  snapshot: IdeaVendorTabRedirectSnapshot | null
): Array<Record<string, unknown>> {
  if (!snapshot?.vendors?.length || ideaKey == null) return [];
  if (Number(snapshot.ideaId) !== ideaKey) return [];
  return snapshot.vendors.map((v) => ({
    id: v.id,
    vendorId: v.id,
    vendorName: v.name,
    vendorCorporateName: v.name,
    vendorDetails: { id: v.id, vendorCorporateName: v.name },
    vendorAssessmentId: "",
    vendorIngredientDetailsId: "",
  }));
}

export type MergeIdeaPanelSnapshotOptions = {
  /**
   * When greater than zero, vendor form rows exist but the mapped list is empty (e.g. filter).
   * Do not overlay snapshot — preserve the empty list.
   */
  sourceFormsLength?: number;
};

export function mergeIdeaPanelMappedVendors<T extends { id?: unknown }>(
  primaryMapped: T[],
  ideaKey: number | null,
  snapshot: IdeaVendorTabRedirectSnapshot | null,
  options?: MergeIdeaPanelSnapshotOptions
): T[] {
  if (Array.isArray(primaryMapped) && primaryMapped.length > 0) return primaryMapped;
  if (typeof options?.sourceFormsLength === "number" && options.sourceFormsLength > 0) {
    return primaryMapped;
  }
  return resolveIdeaPanelVendorsFromSnapshotOnly(ideaKey, snapshot) as T[];
}

/** Shape of `vendorList` in GlobalContext for Tasting Panel bootstrap. */
export function mapIdeaVendorSnapshotToGlobalVendorList(
  ideaKey: number | null,
  snapshot: IdeaVendorTabRedirectSnapshot | null
): { id: string; name: string }[] {
  const rows = resolveIdeaPanelVendorsFromSnapshotOnly(ideaKey, snapshot);
  return rows
    .map((r) => ({
      id: String((r as { id?: unknown }).id ?? ""),
      name: String((r as { vendorName?: unknown }).vendorName ?? "Vendor"),
    }))
    .filter((x) => x.id !== "");
}
